import { FC } from 'react';
import { Box, VStack, HStack, Text, Button, Badge, Separator } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
} from '@/components/ui/dialog';
import { FiCheck, FiX, FiAlertTriangle } from 'react-icons/fi';
import { usePDFAnalysisStore, isPedigreeData, isMicrochipData, isGeneticTestData, isUnifiedData } from '@/stores/pdfAnalysisStore';
import {
  DOCUMENT_TYPE_LABELS,
  GENETIC_TEST_RESULT_LABELS,
  GENETIC_TEST_RESULT_COLORS,
  ExtractedPedigreeData,
  ExtractedMicrochipData,
  ExtractedGeneticTestData,
  ExtractedUnifiedData,
} from '@/types/pdfAnalysis';
import { PDF_ANALYSIS_DISCLAIMER } from '@/services/ai/config';

interface AnalysisResultPreviewProps {
  /** フォームに反映するコールバック */
  onApply: (data: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData) => void;
  /** キャンセル時のコールバック */
  onCancel: () => void;
}

/**
 * 解析結果プレビューモーダル
 * 抽出されたデータを確認し、フォームに反映する前に編集可能
 */
export const AnalysisResultPreview: FC<AnalysisResultPreviewProps> = ({
  onApply,
  onCancel,
}) => {
  const { result, isPreviewOpen, closePreview } = usePDFAnalysisStore();

  // resultからデータを取得（オプショナルチェーン使用）
  const documentType = result?.documentType;
  const extractedData = result?.extractedData;
  const confidence = result?.confidence ?? 0;
  const warnings = result?.warnings;

  /**
   * 適用ボタンクリック
   */
  const handleApply = () => {
    if (result?.success && extractedData) {
      onApply(extractedData);
    }
    closePreview();
  };

  /**
   * キャンセルボタンクリック
   */
  const handleCancel = () => {
    onCancel();
    closePreview();
  };

  /**
   * 信頼度バッジの色を取得
   */
  const getConfidenceColor = (conf: number): string => {
    if (conf >= 80) return 'green';
    if (conf >= 60) return 'yellow';
    return 'red';
  };

  /**
   * 血統書データをレンダリング
   */
  const renderPedigreeData = (data: ExtractedPedigreeData) => (
    <VStack gap={3} align="stretch">
      <DataRow label="正式名称" value={data.officialName} />
      <DataRow label="犬種" value={data.breed} />
      <DataRow label="JKC登録番号" value={data.jkcRegistrationNumber} />
      <DataRow label="DNA登録番号" value={data.dnaRegistrationNumber} />
      <DataRow label="性別" value={data.gender === 'male' ? 'オス' : data.gender === 'female' ? 'メス' : undefined} />
      <DataRow label="生年月日" value={data.birthDate} />
      <DataRow label="毛色" value={data.color} />
      <DataRow label="繁殖者名" value={data.breederName} />
      <DataRow label="所有者名" value={data.ownerName} />

      {data.ancestors && (
        <>
          <Separator my={2} />
          <Text fontWeight="medium" fontSize="sm" color="gray.700">
            血統情報
          </Text>
          {data.ancestors.father && (
            <DataRow
              label="父犬"
              value={`${data.ancestors.father.name || '不明'}${data.ancestors.father.registrationNumber ? ` (${data.ancestors.father.registrationNumber})` : ''}`}
            />
          )}
          {data.ancestors.mother && (
            <DataRow
              label="母犬"
              value={`${data.ancestors.mother.name || '不明'}${data.ancestors.mother.registrationNumber ? ` (${data.ancestors.mother.registrationNumber})` : ''}`}
            />
          )}
          {data.ancestors.paternalGrandfather && (
            <DataRow
              label="父方祖父"
              value={`${data.ancestors.paternalGrandfather.name || '不明'}${data.ancestors.paternalGrandfather.registrationNumber ? ` (${data.ancestors.paternalGrandfather.registrationNumber})` : ''}`}
            />
          )}
          {data.ancestors.paternalGrandmother && (
            <DataRow
              label="父方祖母"
              value={`${data.ancestors.paternalGrandmother.name || '不明'}${data.ancestors.paternalGrandmother.registrationNumber ? ` (${data.ancestors.paternalGrandmother.registrationNumber})` : ''}`}
            />
          )}
          {data.ancestors.maternalGrandfather && (
            <DataRow
              label="母方祖父"
              value={`${data.ancestors.maternalGrandfather.name || '不明'}${data.ancestors.maternalGrandfather.registrationNumber ? ` (${data.ancestors.maternalGrandfather.registrationNumber})` : ''}`}
            />
          )}
          {data.ancestors.maternalGrandmother && (
            <DataRow
              label="母方祖母"
              value={`${data.ancestors.maternalGrandmother.name || '不明'}${data.ancestors.maternalGrandmother.registrationNumber ? ` (${data.ancestors.maternalGrandmother.registrationNumber})` : ''}`}
            />
          )}
        </>
      )}
    </VStack>
  );

  /**
   * マイクロチップデータをレンダリング
   */
  const renderMicrochipData = (data: ExtractedMicrochipData) => (
    <VStack gap={3} align="stretch">
      <DataRow label="マイクロチップ番号" value={data.microchipNumber} highlight />
      <DataRow label="登録日" value={data.registrationDate} />
      <DataRow label="暗証記号" value={data.pin} />
    </VStack>
  );

  /**
   * 遺伝子検査データをレンダリング
   */
  const renderGeneticTestData = (data: ExtractedGeneticTestData) => (
    <VStack gap={3} align="stretch">
      <DataRow label="どうぶつ名" value={data.animalName} highlight />
      <DataRow label="検査日" value={data.testDate} />
      <DataRow label="検査機関" value={data.laboratory} />

      {data.testResults.length > 0 && (
        <>
          <Separator my={2} />
          <Text fontWeight="medium" fontSize="sm" color="gray.700">
            検査結果
          </Text>
          <Box>
            {data.testResults.map((item, index) => (
              <HStack
                key={index}
                justify="space-between"
                py={2}
                borderBottom="1px"
                borderBottomColor="gray.100"
              >
                <Text fontSize="sm">{item.testName}</Text>
                <Badge colorPalette={GENETIC_TEST_RESULT_COLORS[item.result]}>
                  {GENETIC_TEST_RESULT_LABELS[item.result]}
                </Badge>
              </HStack>
            ))}
          </Box>
        </>
      )}
    </VStack>
  );

  /**
   * 統合データをレンダリング（血統書+マイクロチップ+遺伝子検査）
   */
  const renderUnifiedData = (data: ExtractedUnifiedData) => (
    <VStack gap={4} align="stretch">
      {/* 血統書情報 */}
      {renderPedigreeData(data)}

      {/* マイクロチップ情報（追加） */}
      {data.microchipNumber && (
        <Box borderTop="1px" borderColor="gray.200" pt={4}>
          <Text fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
            マイクロチップ情報
          </Text>
          <DataRow label="マイクロチップ番号" value={data.microchipNumber} highlight />
          {data.microchipRegistrationDate && (
            <DataRow label="登録日" value={data.microchipRegistrationDate} />
          )}
          {data.microchipPin && (
            <DataRow label="暗証記号" value={data.microchipPin} />
          )}
        </Box>
      )}

      {/* 遺伝子検査情報（追加） */}
      {data.geneticTestResults && data.geneticTestResults.length > 0 && (
        <Box borderTop="1px" borderColor="gray.200" pt={4}>
          <Text fontWeight="medium" fontSize="sm" color="gray.700" mb={2}>
            遺伝子検査結果
          </Text>
          {data.animalName && (
            <DataRow label="名前" value={data.animalName} highlight />
          )}
          {data.geneticTestDate && (
            <DataRow label="検査日" value={data.geneticTestDate} />
          )}
          {data.geneticTestLaboratory && (
            <DataRow label="検査機関" value={data.geneticTestLaboratory} />
          )}
          <Box mt={2}>
            {data.geneticTestResults.map((test, index) => (
              <HStack
                key={index}
                justify="space-between"
                py={2}
                borderBottom="1px"
                borderBottomColor="gray.100"
              >
                <Text fontSize="sm">{test.testName}</Text>
                <Badge colorPalette={GENETIC_TEST_RESULT_COLORS[test.result]}>
                  {GENETIC_TEST_RESULT_LABELS[test.result]}
                </Badge>
              </HStack>
            ))}
          </Box>
        </Box>
      )}
    </VStack>
  );

  // DialogRootは常にマウント（open propで表示制御）
  return (
    <DialogRoot
      open={isPreviewOpen && result?.success === true}
      onOpenChange={(e) => !e.open && handleCancel()}
      closeOnInteractOutside={false}
      size="lg"
    >
      <DialogContent
        maxW="lg"
        maxHeight="90vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {result?.success && documentType && extractedData && (
          <>
            <DialogHeader>
              <DialogTitle>
                <HStack gap={2}>
                  <Text>解析結果の確認</Text>
                  <Badge colorPalette="blue">{DOCUMENT_TYPE_LABELS[documentType]}</Badge>
                </HStack>
              </DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody flex="1" overflow="auto">
              <VStack gap={4} align="stretch">
                {/* 信頼度 */}
                <HStack justify="space-between" p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600">
                    認識精度
                  </Text>
                  <Badge colorPalette={getConfidenceColor(confidence)} size="lg">
                    {confidence}%
                  </Badge>
                </HStack>

                {/* 警告 */}
                {warnings && warnings.length > 0 && (
                  <Box p={3} bg="yellow.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="yellow.400">
                    <HStack gap={2} mb={2}>
                      <FiAlertTriangle color="#D69E2E" />
                      <Text fontSize="sm" fontWeight="medium" color="yellow.700">
                        注意事項
                      </Text>
                    </HStack>
                    <VStack align="start" gap={1}>
                      {warnings.map((warning, index) => (
                        <Text key={index} fontSize="xs" color="yellow.700">
                          ・{warning}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* 抽出データ */}
                <Box p={4} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                  {isUnifiedData(extractedData) && renderUnifiedData(extractedData)}
                  {!isUnifiedData(extractedData) && isPedigreeData(extractedData) && renderPedigreeData(extractedData)}
                  {isMicrochipData(extractedData) && renderMicrochipData(extractedData)}
                  {isGeneticTestData(extractedData) && renderGeneticTestData(extractedData)}
                </Box>

                {/* 免責事項 */}
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  {PDF_ANALYSIS_DISCLAIMER}
                </Text>
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={2}>
                <Button variant="outline" onClick={handleCancel}>
                  <HStack gap={1}>
                    <FiX />
                    <Text>キャンセル</Text>
                  </HStack>
                </Button>
                <Button colorPalette="blue" onClick={handleApply}>
                  <HStack gap={1}>
                    <FiCheck />
                    <Text>フォームに反映</Text>
                  </HStack>
                </Button>
              </HStack>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </DialogRoot>
  );
};

/**
 * データ行コンポーネント
 */
interface DataRowProps {
  label: string;
  value?: string;
  highlight?: boolean;
}

const DataRow: FC<DataRowProps> = ({ label, value, highlight }) => (
  <HStack justify="space-between" py={1}>
    <Text fontSize="sm" color="gray.600">
      {label}
    </Text>
    <Text
      fontSize="sm"
      fontWeight={highlight ? 'bold' : 'medium'}
      color={value ? 'gray.800' : 'gray.400'}
    >
      {value || '未取得'}
    </Text>
  </HStack>
);
